/**
 * TowerHorizon.tsx
 *
 * W66.C — Methodology Workspace — Horizon 3: Tower View
 *
 * Preview panel for the M-PACT Pipeline Tower (/pipeline-tower).
 *
 * PipelineTower and Tower both require live Supabase deal data and render
 * a complex multi-deal architectural visualization. Embedding them inline
 * causes layout breakage and performance issues in a stacked page context.
 * Thumbnail + CTA used per W66.C HARD STOP rule.
 *
 * CANON: No exclamation points. No emojis. M-PACT hyphenated.
 */

import { Link } from "wouter";
import { HELIX_CHECKPOINTS } from "@/lib/helix-checkpoints";

// ─── Static tower diagram ─────────────────────────────────────────────────────

function TowerDiagram() {
  const floors = [...HELIX_CHECKPOINTS].reverse(); // CP10 at top, CP1 at base

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
      {floors.map((cp, idx) => {
        const widthPct = 100 - idx * 3;
        return (
          <div
            key={cp.id}
            style={{
              width: `${widthPct}%`,
              alignSelf: "center",
              background: idx === 0 ? "#DC2626" : idx < 3 ? "#1F1F23" : "#0B0B0F",
              borderRadius: 4,
              padding: "5px 10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <span style={{ fontSize: 10, fontWeight: 700, color: "#FAFAF7", whiteSpace: "nowrap" }}>
              L{cp.number} — {cp.name}
            </span>
            <span style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", whiteSpace: "nowrap" }}>
              {cp.weight}%
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Horizon component ────────────────────────────────────────────────────────

export default function TowerHorizon() {
  return (
    <section>
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}
        className="methodology-tower-grid"
      >
        {/* Left — tower diagram */}
        <div
          style={{
            background: "#0B0B0F",
            borderRadius: 10,
            padding: "24px 20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.4)",
              marginBottom: 16,
              alignSelf: "flex-start",
            }}
          >
            Tower — floor layout (CP1 base to CP10 roof)
          </div>
          <TowerDiagram />
          <div style={{ marginTop: 14, fontSize: 10, color: "rgba(255,255,255,0.35)", textAlign: "center" }}>
            Floor height reflects checkpoint sequence, not weight.
            Live deal positions render in /pipeline-tower.
          </div>
        </div>

        {/* Right — description + CTAs */}
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E5E5E7",
            borderRadius: 10,
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#6B6B70",
                marginBottom: 10,
              }}
            >
              About this view
            </div>
            <h3
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#0B0B0F",
                margin: "0 0 10px",
                letterSpacing: "-0.01em",
              }}
            >
              Pipeline Tower
            </h3>
            <p style={{ fontSize: 13, color: "#3D3D42", lineHeight: 1.65, margin: "0 0 16px" }}>
              The Tower is an architectural visualization of the entire book of business.
              Each deal appears as an orb on the floor (checkpoint level) it currently occupies.
              The Tower makes it immediately visible which checkpoints are crowded, which are
              empty, and where the pipeline is stalling across all active opportunities.
            </p>
            <ul style={{ margin: "0 0 20px", paddingLeft: 18, fontSize: 12, color: "#6B6B70", lineHeight: 1.7 }}>
              <li>Multi-deal aggregate view across the full pipeline</li>
              <li>Floor 1 (CP1) at the base; Floor 10 (CP10) at the roof</li>
              <li>Deals that exit the building appear in the fire escape column</li>
              <li>Single-deal view with side-by-side Helix available at /tower</li>
            </ul>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Link
              href="/pipeline-tower"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                background: "#0B0B0F",
                color: "#FAFAF7",
                borderRadius: 8,
                padding: "11px 20px",
                fontSize: 13,
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              Open full view — Pipeline Tower
            </Link>
            <Link
              href="/tower"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                background: "#FFFFFF",
                color: "#0B0B0F",
                border: "1px solid #E5E5E7",
                borderRadius: 8,
                padding: "9px 20px",
                fontSize: 13,
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              Single-deal view — /tower
            </Link>
            <p style={{ fontSize: 11, color: "#9CA3AF", margin: 0 }}>
              Direct links: /pipeline-tower · /tower
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .methodology-tower-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
